/**
 * Offline-safe queue for goods receipts. Drafts are stored in localStorage
 * and auto-retried when the network returns. Non-network errors (validation)
 * are surfaced immediately; genuine network errors keep the item queued.
 */
import { useEffect, useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";

const KEY = "essence.offline.receipts.v1";

const load = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); }
  catch { return []; }
};
const save = (arr) => localStorage.setItem(KEY, JSON.stringify(arr));

const isNetworkError = (e) => {
  // axios: no response => network error
  if (!e?.response) return true;
  const s = e.response.status;
  return s === 0 || s === 502 || s === 503 || s === 504;
};

let flushing = false;

export function useOfflineReceiptQueue() {
  const [queue, setQueue] = useState(load());
  const [online, setOnline] = useState(navigator.onLine);

  const refresh = useCallback(() => setQueue(load()), []);

  const enqueue = useCallback((payload) => {
    const arr = load();
    const item = { id: crypto.randomUUID(), payload, queued_at: new Date().toISOString(), attempts: 0, last_error: null };
    arr.push(item);
    save(arr);
    setQueue(arr);
    return item;
  }, []);

  const removeItem = useCallback((id) => {
    const arr = load().filter(x => x.id !== id);
    save(arr); setQueue(arr);
  }, []);

  const flush = useCallback(async () => {
    if (flushing) return;
    flushing = true;
    try {
      let arr = load();
      let changed = false;
      for (const item of [...arr]) {
        try {
          await api.post("/receipts", item.payload);
          arr = arr.filter(x => x.id !== item.id);
          save(arr);
          changed = true;
          toast.success(`Queued receipt synced (${item.payload.declared_qty} of item)`);
        } catch (e) {
          item.attempts = (item.attempts || 0) + 1;
          item.last_error = formatError(e);
          save(arr);
          if (!isNetworkError(e)) {
            // Non-network error — leave in queue but stop batching so the user notices
            changed = true;
            break;
          }
          // network — stop, will retry later
          break;
        }
      }
      if (changed) setQueue(load());
    } finally { flushing = false; }
  }, []);

  const submitOrQueue = useCallback(async (payload) => {
    if (!navigator.onLine) {
      const it = enqueue(payload);
      toast.info("You're offline — receipt queued");
      return { queued: true, id: it.id };
    }
    try {
      const { data } = await api.post("/receipts", payload);
      return { queued: false, data };
    } catch (e) {
      if (isNetworkError(e)) {
        const it = enqueue(payload);
        toast.warning("Network hiccup — receipt queued");
        return { queued: true, id: it.id };
      }
      throw e;
    }
  }, [enqueue]);

  useEffect(() => {
    const onOn = () => { setOnline(true); flush(); };
    const onOff = () => setOnline(false);
    window.addEventListener("online", onOn);
    window.addEventListener("offline", onOff);
    // background retry every 20s
    const t = setInterval(() => { if (navigator.onLine) flush(); }, 20_000);
    // try once at mount
    if (navigator.onLine) flush();
    return () => { window.removeEventListener("online", onOn); window.removeEventListener("offline", onOff); clearInterval(t); };
  }, [flush]);

  return { queue, online, submitOrQueue, flush, removeItem, refresh };
}
